'use client';
import React from 'react';

import type { CampaignFormData } from './CampaignBuilderClient';

const wilayas = ['Alger', 'Oran', 'Constantine', 'Annaba', 'Blida', 'Sétif', 'Tlemcen', 'Batna', 'Béjaïa', 'Tizi Ouzou', 'Toute l\'Algérie'];
const languages = ['Arabe', 'Darija', 'Français', 'Anglais', 'Tamazight'];
const creatorCats = ['Lifestyle', 'Beauty', 'Tech', 'Food', 'Fashion', 'Comedy', 'Sport', 'Family', 'Gaming', 'Education', 'Business', 'UGC'];

interface Props { form: UseFormReturn<CampaignFormData>; }

export default function StepAudience({ form }: Props) {
  const { register, watch, setValue, formState: { errors } } = form;
  const targetWilaya = watch('targetWilaya');
  const targetLanguage = watch('targetLanguage');
  const creatorCategories = watch('creatorCategories');

  const toggleArr = (key: 'targetWilaya' | 'targetLanguage' | 'creatorCategories', val: string) => {
    const arr = watch(key) as string[];
    setValue(key, arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val]);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-700 text-foreground mb-1">Audience cible</h2>
        <p className="text-sm text-muted-foreground">Définissez le profil de l'audience que vous souhaitez toucher.</p>
      </div>

      {/* Age Range */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-600 text-foreground mb-1">Âge minimum</label>
          <input
            type="number"
            {...register('targetAgeMin', { min: 13, max: 65 })}
            className="w-full bg-input border border-border rounded-lg px-4 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-600 text-foreground mb-1">Âge maximum</label>
          <input
            type="number"
            {...register('targetAgeMax', { min: 13, max: 65 })}
            className="w-full bg-input border border-border rounded-lg px-4 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors"
          />
        </div>
      </div>

      {/* Gender */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-2">Genre cible</label>
        <div className="flex gap-3">
          {[{ id: 'tg-all', val: '', label: 'Tous' }, { id: 'tg-f', val: 'F', label: 'Femme' }, { id: 'tg-m', val: 'M', label: 'Homme' }].map(g => (
            <button
              key={g.id}
              type="button"
              onClick={() => setValue('targetGender', g.val)}
              className={`flex-1 py-2.5 rounded-lg text-sm font-600 transition-all ${
                watch('targetGender') === g.val
                  ? 'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {/* Wilayas */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-2">Zone géographique</label>
        <div className="flex flex-wrap gap-2">
          {wilayas.map(w => (
            <button
              key={`tw-${w}`}
              type="button"
              onClick={() => toggleArr('targetWilaya', w)}
              className={`px-3 py-1.5 rounded-full text-xs font-500 transition-all ${
                targetWilaya.includes(w)
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {w}
            </button>
          ))}
        </div>
      </div>

      {/* Languages */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-2">Langues de communication</label>
        <div className="flex flex-wrap gap-2">
          {languages.map(l => (
            <button
              key={`tl-${l}`}
              type="button"
              onClick={() => toggleArr('targetLanguage', l)}
              className={`px-3 py-1.5 rounded-full text-xs font-500 transition-all ${
                targetLanguage.includes(l)
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Creator Categories */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-2">Catégories de créateurs souhaitées</label>
        <div className="flex flex-wrap gap-2">
          {creatorCats.map(c => (
            <button
              key={`cc-${c}`}
              type="button"
              onClick={() => toggleArr('creatorCategories', c)}
              className={`px-3 py-1.5 rounded-full text-xs font-500 transition-all ${
                creatorCategories.includes(c)
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}